import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { TagListBuilder } from '../../../../components/entities/tags/TagListBuilder';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Column } from '../../../../components-v2/PageLayout';
import { useCompaniesPageUrlState } from '..';
import { addTag, removeTag } from '../companyTagFilter';
import { styleSheet } from './styles';

/**
 * Advanced tag filter drawer for the companies page. Builds OR / AND / NOT tag lists that the
 * container turns into a recursive POST company/filter/v2 request. State lives entirely in the URL
 * (nuqs) so filters are shareable and survive refresh.
 */
export const CompanyTagFiltersDrawer: React.FC = () => {
	const [{ companyOrTags, companyAndTags, companyNotTags }, setUrlState] = useCompaniesPageUrlState();

	const [showAndTags, setShowAndTags] = React.useState(companyAndTags.length > 0);
	const [showNotTags, setShowNotTags] = React.useState(companyNotTags.length > 0);

	// Keep each checkbox in sync with its tag list, including when the list is cleared externally
	// (e.g. the search bar "Clear search"). Split into two effects so toggling one section open with
	// an empty list isn't collapsed when the other list changes.
	React.useEffect(() => {
		setShowAndTags(companyAndTags.length > 0);
	}, [companyAndTags.length]);
	React.useEffect(() => {
		setShowNotTags(companyNotTags.length > 0);
	}, [companyNotTags.length]);

	const handleAndCheckChange = (checked: boolean | 'indeterminate') => {
		const isChecked = checked === true;
		setShowAndTags(isChecked);
		if (!isChecked && companyAndTags.length) {
			setUrlState({ companyAndTags: [] });
		}
	};

	const handleNotCheckChange = (checked: boolean | 'indeterminate') => {
		const isChecked = checked === true;
		setShowNotTags(isChecked);
		if (!isChecked && companyNotTags.length) {
			setUrlState({ companyNotTags: [] });
		}
	};

	const handleClearFilters = () => {
		// Clear the tag filters and the composed name search so nothing is left filtering the grid.
		setUrlState({
			companyOrTags: [],
			companyAndTags: [],
			companyNotTags: [],
			companySearchValue: '',
			companySearchProperty: Api.CompanySearchRequestProperty.Name,
		});
		setShowAndTags(false);
		setShowNotTags(false);
	};

	return (
		<Column styles={[styleSheet.drawerContainer]} scrollable>
			<h3 className={css(styleSheet.heading)}>Tags</h3>
			<Column styles={[styleSheet.gap]}>
				<p className={css(styleSheet.textBase)}>Included tag(s):</p>
				<TagListBuilder
					styles={[styleSheet.tagListBuilderContainer]}
					inputId='company-or-tags'
					onTagAdded={value => setUrlState({ companyOrTags: addTag(companyOrTags, value) })}
					onTagRemoved={value => setUrlState({ companyOrTags: removeTag(companyOrTags, value) })}
					separator={Api.FilterOperator.Or}
					tags={companyOrTags}
					openOnFocus
				/>
				<div />
				<p className={css(styleSheet.textBase)}>Narrow the list:</p>
				<div className={css(styleSheet.checkboxRow)}>
					<Checkbox checked={showAndTags} id='company-and-tags' onCheckedChange={handleAndCheckChange} />
					<label className={css(styleSheet.textSm)} htmlFor='company-and-tags'>
						Companies must also have these tags...
					</label>
				</div>
				{showAndTags ? (
					<TagListBuilder
						styles={[styleSheet.tagListBuilderContainer]}
						inputId='add-company-and-tags'
						onTagAdded={value => setUrlState({ companyAndTags: addTag(companyAndTags, value) })}
						onTagRemoved={value => setUrlState({ companyAndTags: removeTag(companyAndTags, value) })}
						separator={Api.FilterOperator.And}
						tags={companyAndTags}
						openOnFocus
					/>
				) : null}
				<div className={css(styleSheet.checkboxRow)}>
					<Checkbox checked={showNotTags} id='company-not-tags' onCheckedChange={handleNotCheckChange} />
					<label className={css(styleSheet.textSm)} htmlFor='company-not-tags'>
						Exclude these tags...
					</label>
				</div>
				{showNotTags ? (
					<TagListBuilder
						styles={[styleSheet.tagListBuilderContainer]}
						inputId='add-company-not-tags'
						onTagAdded={value => setUrlState({ companyNotTags: addTag(companyNotTags, value) })}
						onTagRemoved={value => setUrlState({ companyNotTags: removeTag(companyNotTags, value) })}
						separator={Api.FilterOperator.Not}
						tags={companyNotTags}
						openOnFocus
					/>
				) : null}
			</Column>
			<div className={css(styleSheet.divider)} />
			<Button variant='link' onClick={handleClearFilters}>
				Clear all filters
			</Button>
		</Column>
	);
};
